/// <amd-module name="Build/Run/Components/ChecksCard" />
import * as React from "react";

import * as Resources from "Build/Run/Resources";

import { ITaskCheckLogLineService } from "Build/Common/Library/TaskCheckLogLineService";
import { equals, format } from "VSS/Core/Util/String";

import { CheckReference, ChecksPermission } from "Build/Client/Contracts/DataProvider";
import { CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import { ApprovalCheckTypeId, AuthorizationCheckTypeIdString, ManualValidationTaskTypeIdString, TaskCheckTypeIdString } from "Build/Run/Constants";
import { IVssPageContext } from "VSS/Platform/Context";
import { IButtonProps } from "VSSUI/Button";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";

export interface IStageCheckData {
    id: string;
    name: string;
    checks: CheckReference[];
}

export interface IChecksCardProps {
    className?: string;
    stages: IStageCheckData[];
    onReviewClicked: (stages: IStageCheckData[]) => void;
    pageContext: IVssPageContext;
    projectId: string;
    useUpdatedChecksFormat?: boolean;
    isChecksLogsDisabled?: boolean;
    isAsyncCheckLogsFlowEnabled?: boolean;
}

const defaultStageName = "__default";

export const ChecksCard: React.FunctionComponent<IChecksCardProps> = props => {
    const taskCheckLogLineService = props.pageContext.getService<ITaskCheckLogLineService>("ITaskCheckLogLineService");
    const [pendingStages, setPendingStages] = React.useState([] as IStageCheckData[]);
    const [failedStages, setFailedStages] = React.useState([] as IStageCheckData[]);
    const [approvals, setApprovals] = React.useState(0);
    const [manualValidations, setManualValidations] = React.useState(0);
    const [myManualValidations, setMyManualValidations] = React.useState(0);
    const [pendingItems, setPendingItems] = React.useState(0);
    const [failedItems, setFailedItems] = React.useState(0);
    const [itemsForMe, setItemsForMe] = React.useState(0);
    const [permissions, setPermissions] = React.useState(0);
    const [taskItems, setTaskItems] = React.useState(0);
    const [logStageName, setLogStageName] = React.useState("");
    const [logCheckName, setLogCheckName] = React.useState("");
    const [logLine, setLogLine] = React.useState("");

    React.useEffect(() => {
        let itemsForMe = 0;
        let allPendingItems = 0;
        let allFailedItems = 0;
        let approvalsCount = 0;
        let manualValidationsCount = 0;
        let myManualValidationsCount = 0;
        let permissionsCount = 0;
        let taskCount = 0;
        let stagesWithPendingItems: IStageCheckData[] = [];
        let stagesWithFailedItems: IStageCheckData[] = [];
        let lastChanged = new Date(0);

        for (const s of props.stages) {
            let hasPendingItems = false;
            let hasFailedItems = false;

            for (const c of s.checks || []) {
                if (c.permissions === ChecksPermission.Update) {
                    itemsForMe++;
                    if (equals(c.typeId, ManualValidationTaskTypeIdString, true) && c.status === CheckRunStatus.Running) {
                        myManualValidationsCount++;
                    }
                }

                switch (c.status) {
                    case CheckRunStatus.Running:
                        allPendingItems++;
                        hasPendingItems = true;

                        switch (true) {
                            case equals(c.typeId, ApprovalCheckTypeId, true):
                                approvalsCount++;
                                break;
                            case equals(c.typeId, AuthorizationCheckTypeIdString, true):
                                permissionsCount++;
                                break;
                            case equals(c.typeId, ManualValidationTaskTypeIdString, true):
                                manualValidationsCount++;
                                break;
                            case equals(c.typeId, TaskCheckTypeIdString, true):
                                taskCount++;
                                break;
                        }
                        break;

                    case CheckRunStatus.Failed:
                        allFailedItems++;
                        hasFailedItems = true;
                        break;
                    case CheckRunStatus.Rejected:
                        if (equals(c.typeId, TaskCheckTypeIdString, true)) {
                            allFailedItems++;
                            hasFailedItems = true;
                        }
                        break;
                }

                if (!props.isChecksLogsDisabled && equals(c.typeId, TaskCheckTypeIdString, true)) {
                    if (props.isAsyncCheckLogsFlowEnabled) {
                        if (c.lastChanged > lastChanged) {
                            lastChanged = c.lastChanged;
                            setLogStageName(s.name);
                            setLogCheckName(c.name);                            
                            taskCheckLogLineService.getLastCheckLogLine(props.pageContext, c.planId, c.logId, props.projectId, c.lineCount).then((log) => {
                                setLogLine(log);
                            });
                        }
                    } else {
                        if (c.lastChanged > lastChanged && c.currentLogLine != "") {
                            lastChanged = c.lastChanged;
                            setLogStageName(s.name);
                            setLogCheckName(c.name);  
                            setLogLine(c.currentLogLine);                    
                        }
                    }                    
                }
            }

            if (hasPendingItems) {
                stagesWithPendingItems.push(s);
            }

            if (hasFailedItems) {
                stagesWithFailedItems.push(s);
            }
        }

        setPendingStages(stagesWithPendingItems);
        setPendingItems(allPendingItems);
        setItemsForMe(itemsForMe);
        setPermissions(permissionsCount);
        setApprovals(approvalsCount);
        setManualValidations(manualValidationsCount);
        setMyManualValidations(myManualValidationsCount);
        setFailedStages(stagesWithFailedItems);
        setFailedItems(allFailedItems);
        setTaskItems(taskCount);

    }, [props.stages]);

    // grab the first one
    const stage = pendingStages[0];
    const showFailedChecks = (props.useUpdatedChecksFormat && failedStages.length > 0)

    // show checks card if there are either pending stages or there are failed checks
    if (stage || showFailedChecks) {

        const onReviewStageClicked = () => {
            if (stage) {
                props.onReviewClicked(pendingStages);
            }
            else {
                props.onReviewClicked(failedStages);
            }
        }

        let message = stage ? getMessage(pendingStages, pendingItems, itemsForMe, approvals, permissions, manualValidations, myManualValidations) : getFailedMessage(failedItems);
        const buttonString = (itemsForMe > 0 || showFailedChecks)? Resources.ChecksCard_Review : Resources.ChecksCard_View;
        const isPrimaryButton = !!(itemsForMe > 0);

        const buttonProps: IButtonProps[] = [{
            onClick: onReviewStageClicked,
            primary: isPrimaryButton,
            text: buttonString
        }];

        const showLog = props.useUpdatedChecksFormat && !props.isChecksLogsDisabled && logLine != "" && (taskItems > 0 || showFailedChecks);
        let log = ""

        if (showLog)
        {
            log = logStageName == defaultStageName ?
                    format(Resources.ChecksCard_Log, logCheckName, logLine) :
                    format(Resources.ChecksCard_LogWithStage, logStageName, logCheckName, logLine);
        }

        return (
            <MessageCard
                buttonProps={buttonProps}
                className="pipelines-run-checks-message"
                severity={permissions > 0 ? MessageCardSeverity.Warning : (stage ? MessageCardSeverity.Info : (showFailedChecks ? MessageCardSeverity.Error : MessageCardSeverity.Info))}
            >
                <div className="flex-column">
                    <span>{message}</span>
                    {showLog && (
                        <span className="pipelines-run-checks-log body-s secondary-text">{ log }</span>
                    )}
                </div>
            </MessageCard>
        );
    }
    else {
        // no checks => no card
        return null;
    }
}

function getMessage(stages: IStageCheckData[], pendingItems: number, itemsForMe: number, approvalsCount: number, permissionsCount: number, manualValidationsCount: number, myManualValidationsCount: number): string {
    let message = "";
    const isSingleNamedStage = stages.length === 1 && stages[0].name !== defaultStageName;
    if (itemsForMe > 0) {
        if (itemsForMe === pendingItems) {
            // all items on me
            if (itemsForMe === 1) {
                if (approvalsCount === 1) {
                    // 1  approval is pending and waiting on me
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SingleApprovalNeedYourReviewStage, stages[0].name) : Resources.ChecksCard_Message_SingleApprovalNeedYourReview;
                }
                else if (permissionsCount === 1) {
                    // 1 permission is waiting on me
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SinglePermissionNeedYourReviewStage, stages[0].name) : Resources.ChecksCard_Message_SinglePermissionNeedYourReview;
                }
                else if (manualValidationsCount === 1) {
                    // 1 manual validation is waiting on me
                    message = Resources.ChecksCard_Message_SingleManualValidationNeedYourReview;
                }
                else {
                    // 1 check is pending and waiting on me
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SingleNeedYourReviewStage, stages[0].name) : Resources.ChecksCard_Message_SingleNeedYourReview;
                }
            }
            else {
                if (approvalsCount === pendingItems) {
                    //{n} approvals are pending and waiting on me
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ApprovalNeedYourReviewStage, itemsForMe.toString(), stages[0].name) : format(Resources.ChecksCard_Message_ApprovalNeedYourReview, itemsForMe.toString());
                }
                else if (permissionsCount === pendingItems) {
                    //{n} permissions are pending and waiting on me
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_PermissionNeedYourReviewStage, pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_PermissionNeedYourReview, pendingItems.toString());
                }
                else if (manualValidationsCount === pendingItems) {
                    //{n} manual validations are pending and waiting on me
                    message = format(Resources.ChecksCard_Message_ManualValidationNeedYourReview, itemsForMe.toString());
                }
                else {

                    if (myManualValidationsCount === 1)
                    {
                        if (itemsForMe === 1)
                        {
                            message = Resources.ChecksCard_Message_SingleManualValidationNeedYourReview;
                        }
                        else if (itemsForMe - 1 === 1)
                        {
                            // 1 check and 1 manual validation are pending and waiting on me
                            message = isSingleNamedStage ? format(Resources.ChecksCard_Message_CheckWithSingleManualValidationNeedYourReviewStage, stages[0].name) : format(Resources.ChecksCard_Message_CheckWithSingleManualValidationNeedYourReview);;
                        }
                        else
                        {
                            //{n} checks and 1 manual validation are pending and waiting on me
                            message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ChecksWithSingleManualValidationNeedYourReviewStage, (itemsForMe-1).toString(), stages[0].name) : format(Resources.ChecksCard_Message_ChecksWithSingleManualValidationNeedYourReview, (itemsForMe - 1).toString());
                        }
                    }
                    else if (myManualValidationsCount > 1)
                    {
                        if (itemsForMe - myManualValidationsCount === 1)
                        {
                            //1 check and {m} manual validations are pending and waiting on me
                            message = isSingleNamedStage ? format(Resources.ChecksCard_Message_CheckWithManualValidationNeedYourReviewStage, myManualValidationsCount.toString(), stages[0].name) : format(Resources.ChecksCard_Message_CheckWithManualValidationNeedYourReview, myManualValidationsCount.toString());
                        }
                        else
                        {
                            //{n} checks and {m} manual validations are pending and waiting on me
                            message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ChecksWithManualValidationNeedYourReviewStage, (itemsForMe - myManualValidationsCount).toString(), myManualValidationsCount.toString(), stages[0].name) : format(Resources.ChecksCard_Message_ChecksWithManualValidationNeedYourReview, (itemsForMe - myManualValidationsCount).toString(), myManualValidationsCount.toString());
                        }
                    }
                    else
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_NeedYourReviewStage, itemsForMe.toString(), stages[0].name) : format(Resources.ChecksCard_Message_NeedYourReview, itemsForMe.toString());
                    }
                }
            }
        }
        else {
            // m of n items on me
            if (approvalsCount === pendingItems) {
                // all pending items are approvals
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeApprovalsNeedYourReviewStage, itemsForMe.toString(), pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_SomeApprovalsNeedYourReview, itemsForMe.toString(), pendingItems.toString());
            }
            else if(permissionsCount === pendingItems) {
                // {m} of {n} pending permissions on me
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_PermissionNeedYourReviewStage, pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_PermissionNeedYourReview, pendingItems.toString());
            }
            else if (manualValidationsCount === pendingItems) {
                // all pending items are manual validations
                message = format(Resources.ChecksCard_Message_SomeManualValidationsNeedYourReview, itemsForMe.toString(), pendingItems.toString());
            }
            else {
                // pending items containing a mix of checks
                if (myManualValidationsCount === 1) {
                    if (itemsForMe - 1 === 1)
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeCheckWithSingleManualValidationNeedYourReviewStage, stages[0].name) : format(Resources.ChecksCard_Message_SomeCheckWithSingleManualValidationNeedYourReview);
                    }
                    else
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeChecksWithSingleManualValidationNeedYourReviewStage, (itemsForMe - 1).toString(), stages[0].name) : format(Resources.ChecksCard_Message_SomeChecksWithSingleManualValidationNeedYourReview, (itemsForMe - 1).toString());
                    }
                }
                else if (myManualValidationsCount > 1) {
                    if (itemsForMe - myManualValidationsCount === 1)
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeCheckWithManualValidationsNeedYourReviewStage, myManualValidationsCount.toString(), stages[0].name) : format(Resources.ChecksCard_Message_SomeCheckWithManualValidationsNeedYourReview, myManualValidationsCount.toString());
                    }
                    else
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeChecksWithManualValidationsNeedYourReviewStage, (itemsForMe - myManualValidationsCount).toString(), myManualValidationsCount.toString(), stages[0].name) : format(Resources.ChecksCard_Message_SomeChecksWithManualValidationsNeedYourReview, (itemsForMe - myManualValidationsCount).toString(), myManualValidationsCount.toString());

                    }
                }
                else
                {
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SomeNeedYourReviewStage, itemsForMe.toString(), pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_SomeNeedYourReview, itemsForMe.toString(), pendingItems.toString());
                }
            }
        }
    }
    else {
        // No items on me
        if (pendingItems === 1) {
            // 1 item not on me
            if (approvalsCount === 1) {
                //1 item is an approval
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SingleApprovalNeedReviewStage, stages[0].name) : Resources.ChecksCard_Message_SingleApprovalNeedReview;
            }
            else if(permissionsCount === 1) {
                // 1 permission is pending on others
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SinglePermissionNeedYourReviewStage, stages[0].name) : Resources.ChecksCard_Message_SinglePermissionNeedYourReview;
            }
            else if (manualValidationsCount === 1) {
                //1 item is an MV
                message =  Resources.ChecksCard_Message_SingleManualValidationNeedReview;
            }
            else {
                //1 item is a check
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_SingleNeedReviewStage, stages[0].name) : Resources.ChecksCard_Message_SingleNeedReview;
            }
        }
        else {
            // n items not on me
            if (approvalsCount === pendingItems) {
                // n approvals not on me
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ApprovalNeedReviewStage, pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_ApprovalNeedReview, pendingItems.toString());
            }
            else if(permissionsCount === pendingItems ) {
                // n permissions not on me
                message = isSingleNamedStage ? format(Resources.ChecksCard_Message_PermissionNeedYourReviewStage, pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_PermissionNeedYourReview, pendingItems.toString());
            }
            else if (manualValidationsCount === pendingItems) {
                // n MVs not on me
                message = format(Resources.ChecksCard_Message_ManualValidationsNeedReview, pendingItems.toString());
            }
            else
            {
                // n checks not on me
                let otherManualValidationCount = manualValidationsCount - myManualValidationsCount;
                if ( otherManualValidationCount === 1) {
                    if (pendingItems - 1 === 1)
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_CheckWithSingleManualValidationNeedReviewStage, stages[0].name) : format(Resources.ChecksCard_Message_CheckWithSingleManualValidationNeedReview);
                    }
                    else
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ChecksWithSingleManualValidationNeedReviewStage, (pendingItems - 1).toString(), stages[0].name) : format(Resources.ChecksCard_Message_ChecksWithSingleManualValidationNeedReview, (pendingItems - 1).toString());
                    }
                }
                else if (manualValidationsCount - myManualValidationsCount > 1) {
                    if (pendingItems - otherManualValidationCount === 1)
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_CheckWithManualValidationsNeedReviewStage, (manualValidationsCount - myManualValidationsCount).toString(), stages[0].name) : format(Resources.ChecksCard_Message_CheckWithManualValidationsNeedReview, (manualValidationsCount - myManualValidationsCount).toString());
                    }
                    else
                    {
                        message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ChecksWithManualValidationsNeedReviewStage, (pendingItems - manualValidationsCount).toString(), (manualValidationsCount - myManualValidationsCount).toString(), stages[0].name) : format(Resources.ChecksCard_Message_ChecksWithManualValidationsNeedReview, (pendingItems - myManualValidationsCount).toString(), (manualValidationsCount - myManualValidationsCount).toString());

                    }
                }
                else
                {
                    message = isSingleNamedStage ? format(Resources.ChecksCard_Message_ChecksNeedReviewStage, pendingItems.toString(), stages[0].name) : format(Resources.ChecksCard_Message_ChecksNeedReview, pendingItems.toString());
                }
            }
        }
    }
    return message;
}

function getFailedMessage(failedItems: number): string {
    let message = "";

    if (failedItems === 1) {
        message = Resources.ChecksCard_Message_SingleFailedCheck;
    }
    else {
        message = format(Resources.ChecksCard_Message_FailedChecks, failedItems.toString());
    }

    return message;
}